import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import DatePickerCalendar from './DatePickerCalendar';
import ProductPickerPopover from './ProductPickerPopover';
import ProductSearchDropdown from './ProductSearchDropdown';
import { TrashIcon } from './ActionIcons';
import { nfConfirm } from './confirm';
import {
  ConflictNote, HiddenDefectModal, ReceivingLineEditor, ReceivingLineSummary, RowMenu,
  hiddenText, lineTitle, marks, photoSrc, useNarrow,
} from './ProcurementReceiving';

/**
 * Накладная из биржи и приёмка по строкам на старых экранах склада (07.11.2026, решение владельца:
 * «старый удобный интерфейс — основа, биржа только заполняет поля»):
 *   • ImportLinesView — строки накладной из биржи в окне накладной (только чтение: фото, «с биржи: …», ↳ товары);
 *   • ImportInvoiceForm — «Редактировать» накладную из биржи: та же форма плановой, строки биржи —
 *     заполненными строками (товар — лупой, кол-во ведёт биржа, цена ₽, лимит), строки руками;
 *   • ReceivingAcceptModal — «Принять поставку» для всех плановых (флаг включён): старые колонки
 *     + причина брака, фото, оценки, «принял»; правка прямо в строке, строка раскрывается.
 * Бэкенд — routers/procurement.py, services/procurement_receiving.py.
 */

const fmtRub = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
const fmtCur = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 4 });
const num = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : null; };
const int = (v) => { const n = parseInt(String(v ?? '').replace(/\D/g, ''), 10); return Number.isFinite(n) ? n : null; };
const ddmm = (iso) => { if (!iso) return '—'; const [, m, d] = String(iso).slice(0, 10).split('-'); return `${d}.${m}`; };
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '');
const mskToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const cellInput = { width: '100%', boxSizing: 'border-box', padding: '4px 6px', textAlign: 'center', border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', fontSize: 13 };
const isMixName = (name) => /микс|(^|[^a-z])mix([^a-z]|$)/i.test(name || '');
const srOnly = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 };

function Thumb({ url, size = 40 }) {
  return url
    ? <img src={photoSrc(url)} alt="" width={size} height={size} style={{ width: size, height: size, objectFit: 'cover', display: 'block', '--ch': '4px' }} />
    : <span style={{ display: 'block', width: size, height: size, background: '#F3F4F2', '--ch': '4px' }} />;
}

const lotCaption = (l) => `с биржи: ${l.lot_full_name || l.lot_name || '—'}${l.length_cm ? `, ${l.length_cm} см` : ''}${l.match_label ? ` · ${l.match_label}` : ''}`;

// ── Окно накладной: строки биржи только для чтения ─────────────────────

export function ImportLinesView({ invoiceId, reloadKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    api.getProcurementLines(invoiceId).then(d => { if (alive) setData(d); }).catch(e => { if (alive) setError(e.message || 'Не загрузилось'); });
    return () => { alive = false; };
  }, [invoiceId, reloadKey]);
  if (error) return <div style={{ color: '#B0603C', fontSize: 13 }}>Строки не загрузились: {error}</div>;
  if (!data) return <div className="loading"><div className="spinner"></div></div>;
  const accepted = data.invoice_status === 'accepted';
  const qtyOf = (l) => (accepted && l.actual_quantity != null ? l.actual_quantity : (l.stems || 0));
  const total = data.lines.reduce((s, l) => s + (l.cancelled || l.price_missing ? 0 : qtyOf(l) * l.price), 0);
  return (
    <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#F9F9F6' }}>
            <th style={{ ...th, width: 56 }} aria-label="Фото" />
            <th style={th}>Товар</th>
            <th style={{ ...th, textAlign: 'center', width: 80 }}>{accepted ? 'Заплан.' : 'Кол-во'}</th>
            {accepted && <th style={{ ...th, textAlign: 'center', width: 70 }}>Пришло</th>}
            {accepted && <th style={{ ...th, textAlign: 'center', width: 60 }}>Брак</th>}
            {accepted && <th style={{ ...th, width: 120 }}>Причина</th>}
            <th style={{ ...th, textAlign: 'center', width: 90 }}>Лимит продаж</th>
            <th style={{ ...th, textAlign: 'center', width: 110 }}>Цена закупки</th>
            <th style={{ ...th, textAlign: 'right', width: 100 }}>Сумма</th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map(l => (
            <React.Fragment key={l.id}>
              <tr id={`pline-${l.id}`} data-problem={!l.cancelled && !l.product_id && !l.splits.length && !l.is_mix ? '1' : undefined}
                style={{ borderTop: '1px solid #EBEDEC', color: l.cancelled ? '#aaa' : undefined }}>
                <td style={td}><Thumb url={l.photo_url} /></td>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: l.cancelled ? '#aaa' : (l.product_id || l.splits.length ? '#1C3D2E' : '#B0603C'),
                    textDecoration: l.cancelled ? 'line-through' : 'none' }}>
                    {l.splits.length ? lineTitle(l) : (l.product_name || 'Товар не выбран')}
                  </div>
                  {l.origin === 'manual'
                    ? <div style={{ fontSize: 12, color: '#888' }}>добавлено руками</div>
                    : l.origin === 'extra' ? <div style={{ fontSize: 12, color: '#B0603C' }}>лишнее, не заказывали</div>
                      : <div style={{ fontSize: 12, color: '#888' }}>{l.cancelled ? 'отменено на бирже' : lotCaption(l)}</div>}
                  {l.mix_hint && <div style={{ fontSize: 12, color: '#7E5D00' }}>{l.mix_hint}</div>}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>{l.stems}</td>
                {accepted && <td style={{ ...td, textAlign: 'center', fontWeight: 600 }}>{l.cancelled ? '—' : (l.actual_quantity ?? '—')}</td>}
                {accepted && (
                  <td style={{ ...td, textAlign: 'center', color: l.defect_quantity || l.hidden_defect_stems ? '#CB4D45' : '#999' }}>
                    {l.cancelled ? '—' : (l.defect_quantity || 0)}
                    {l.hidden_defect_stems > 0 && <div style={{ fontSize: 11 }}>+{l.hidden_defect_stems} после</div>}
                  </td>
                )}
                {accepted && <td style={{ ...td, fontSize: 12 }}>{l.defect_reason_label || ''}</td>}
                <td style={{ ...td, textAlign: 'center', color: '#5E6561' }}>{l.sale_limit ?? '—'}</td>
                <td style={{ ...td, textAlign: 'center' }} title={l.manual_price_formula || undefined}>
                  {l.price_missing ? <span style={{ color: '#B0603C' }}>нет цены</span> : `${fmtRub(l.price)} ₽`}
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>
                  {l.cancelled ? '—' : l.price_missing ? '' : `${fmtRub(qtyOf(l) * l.price)} ₽`}
                </td>
              </tr>
              {(l.splits || []).map(sp => (
                <tr key={`s${sp.id}`} style={{ color: l.cancelled ? '#aaa' : undefined }}>
                  <td />
                  <td style={{ ...td, paddingLeft: 26, fontSize: 12, paddingTop: 2 }}>
                    ↳ <span style={{ color: sp.product_id ? '#1C3D2E' : '#B0603C', fontWeight: 500 }}>{sp.product_name || 'без товара'}</span>
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontSize: 12, paddingTop: 2 }}>{sp.quantity || '—'}</td>
                  {accepted && <td style={{ ...td, textAlign: 'center', fontSize: 12, paddingTop: 2 }}>{sp.actual_quantity ?? '—'}</td>}
                  {accepted && <td style={{ ...td, textAlign: 'center', fontSize: 12, paddingTop: 2 }}>{sp.defect_quantity || 0}</td>}
                  {accepted && <td />}
                  <td /><td />
                  <td />
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: 10, background: '#F9F9F6', borderTop: '1px solid #eee', fontWeight: 600, fontSize: 14 }}>
        {data.price_missing_count > 0 && <span style={{ color: '#B0603C', fontWeight: 500 }}>без цены: {data.price_missing_count}</span>}
        <span>Итого: {fmtRub(total)} ₽</span>
      </div>
    </div>
  );
}

// ── «Редактировать» накладную из биржи ─────────────────────────────────

const toFormRow = (l) => ({
  key: `l${l.id}`, line_id: l.id, origin: l.origin, cancelled: l.cancelled, editable: l.editable,
  product_id: l.product_id, product_name: l.product_name, orig_product_id: l.product_id,
  stems: l.stems, quantity: String(l.stems), photo_url: l.photo_url, caption: lotCaption(l),
  is_mix: l.is_mix, suggested_name: l.suggested_name, currency: l.currency,
  price: l.price_missing ? '' : String(l.price).replace('.', ','), orig_price: l.price_missing ? null : l.price,
  manual_caption: lotCaption({ ...l, match_label: 'выбрано вручную' }),
  price_cur_manual: l.price_cur_manual, manual_price: l.manual_price, formula: l.manual_price_formula,
  limit: l.sale_limit ?? '', orig_limit: l.sale_limit ?? '',
  splits: l.splits.length ? l.splits.map(sp => ({ key: `s${sp.id}`, product_id: sp.product_id, product_name: sp.product_name, quantity: String(sp.quantity || 0) })) : null,
  splitsDirty: false,
});

function ProductCell({ row, onPick, footer, title = 'Выбрать товар' }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => (row.line_id ? api.getReceivingProductOptions(row.line_id)
    : api.getProductsAll(true).then(d => ({ similar: [], products: (d.items || d).map(p => ({ id: p.id, name: p.name, is_active: p.is_active !== false, is_hidden: !!p.is_hidden, height_cm: p.height_cm })) }))), [row.line_id]);
  const close = useCallback(() => setOpen(false), []);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontWeight: 600, color: row.product_id ? '#1C3D2E' : '#B0603C' }}>{row.product_name || 'Товар не выбран'}</span>
      <button ref={ref} type="button" title={title} aria-label={title} onClick={() => setOpen(o => !o)}
        style={{ width: 26, height: 26, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1C3D2E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>
      {open && (
        <ProductPickerPopover anchorRef={ref} loadOptions={load} onClose={close} title={title}
          footer={(footer || []).map(f => ({ ...f, onClick: () => { setOpen(false); f.onClick(); } }))}
          onSelect={(p) => { setOpen(false); onPick(p); }} />
      )}
    </span>
  );
}

function SplitAddButton({ lineId, exclude, onAdd }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.getReceivingProductOptions(lineId), [lineId]);
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <button ref={ref} type="button" className="btn-link" style={{ fontSize: 12 }} onClick={() => setOpen(o => !o)}>+ товар</button>
      {open && (
        <ProductPickerPopover anchorRef={ref} loadOptions={load} onClose={close} title="Добавить товары" multi excludeIds={exclude}
          onSelect={(p) => { setOpen(false); onAdd([p]); }} onSelectMany={(ps) => { setOpen(false); onAdd(ps); }} />
      )}
    </>
  );
}

// onClose(changed): changed — что-то уже сохранилось сразу («+ создать», «по цене биржи…», убрали
// «лишнее») — окно накладной и список нужно перечитать (08.11)
export function ImportInvoiceForm({ invoice, onClose, onSaved, showToast }) {
  const [rows, setRows] = useState(null);
  const [removed, setRemoved] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoice_number || '');
  const [comment, setComment] = useState(invoice.comment || '');
  const [expectedDate, setExpectedDate] = useState(invoice.expected_date || '');
  const [extraDays, setExtraDays] = useState(invoice.extra_days || 0);
  const [availablePercent, setAvailablePercent] = useState(invoice.available_percent ?? '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusRow, setFocusRow] = useState(null);       // строка под курсором: там «по цене биржи…»
  const [curDraft, setCurDraft] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const savedNow = useRef(false);   // что-то сохранилось сразу — при закрытии перечитать накладную и список

  const load = useCallback(() => {
    api.getProcurementLines(invoice.id).then(d => { setRows(d.lines.map(toFormRow)); setRemoved([]); })
      .catch(e => showToast(e.message || 'Строки не загрузились', 'error'));
  }, [invoice.id, showToast]);
  useEffect(() => { load(); }, [load]);
  const close = () => onClose(savedNow.current);

  const upd = (key, patch) => { setDirty(true); setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r))); };
  // Сохранённое сразу — только эти поля строки, остальной ввод в строке не трогаем; «не изменено» — тоже
  const patchSaved = (key, patch) => setRows(rs => rs.map(r => (r.key === key ? { ...r, ...patch } : r)));

  // Сохраняется сразу (подписано): «+ создать», «по цене биржи…»
  const createProduct = async (row) => {
    try {
      const res = await api.createProcurementLineProduct(row.line_id);
      const fresh = res.lines.find(x => x.id === row.line_id);
      if (fresh) {
        patchSaved(row.key, { product_id: fresh.product_id, product_name: fresh.product_name, orig_product_id: fresh.product_id,
          suggested_name: fresh.suggested_name, caption: lotCaption(fresh) });
      }
      savedNow.current = true;
      showToast(`Товар «${res.product?.name || row.suggested_name}» создан — скрыт, пока нет цены`);
    } catch (e) {
      const existing = e.response?.data?.existing;
      if (existing) { upd(row.key, { product_id: existing.id, product_name: existing.name }); showToast(`Есть «${existing.name}» — выбран`, 'info'); }
      else showToast(e.message || 'Не создалось', 'error');
    }
  };
  const calcPrice = async (row) => {
    const v = num(curDraft[row.key]);
    if (!v || v <= 0) { showToast('Цена биржи — число больше нуля', 'error'); return; }
    try {
      const res = await api.setProcurementLinePrice(row.line_id, { price_cur: v });
      const fresh = res.lines.find(x => x.id === row.line_id);
      if (fresh) patchSaved(row.key, { price: String(fresh.price).replace('.', ','), orig_price: fresh.price, price_cur_manual: fresh.price_cur_manual, manual_price: fresh.manual_price, formula: fresh.manual_price_formula });
      savedNow.current = true;
      setCurDraft(d => ({ ...d, [row.key]: '' }));
      if (res.repriced_orders > 0) showToast(`Себестоимость пересчитана в заказах: ${res.repriced_orders}`, 'info');
    } catch (e) {
      showToast(e.message || 'Площадка не посчитала — введите цену в ₽', 'error');
    }
  };

  // «Лишнее» (принесли при приёмке) — убирается сразу, как в окне приёмки
  const removeExtra = async (r) => {
    if (!(await nfConfirm(`Убрать «${r.product_name || 'лишнее'}» из поставки?`, { confirmText: 'Убрать' }))) return;
    try {
      await api.deleteReceivingLine(r.line_id);
      savedNow.current = true;
      setRows(rs => rs.filter(x => x.key !== r.key));
    } catch (e) {
      showToast(e.message || 'Не убралось', 'error');
    }
  };

  const startSplit = (row) => upd(row.key, {
    splitsDirty: true,
    splits: row.product_id && !isMixName(row.product_name)
      ? [{ key: `n${Date.now()}`, product_id: row.product_id, product_name: row.product_name, quantity: String(row.stems) }] : [],
  });

  const addManual = (p) => {
    setShowAdd(false);
    setDirty(true);
    setRows(rs => [...rs, { key: `m${Date.now()}`, line_id: null, origin: 'manual', product_id: p.id, product_name: p.name,
      quantity: '1', price: '', limit: '', orig_limit: '', isNew: true, editable: true }]);
  };

  const save = async () => {
    // проверки
    for (const r of rows) {
      if (r.splits && r.splitsDirty) {
        const sum = r.splits.reduce((s, x) => s + (int(x.quantity) || 0), 0);
        if (r.splits.some(x => !x.product_id)) { showToast('В «Несколько товаров» у каждой строки нужен товар', 'error'); return; }
        if (r.splits.length && sum !== r.stems) { showToast(`Разложено ${sum} из ${r.stems} — сумма должна совпасть с количеством биржи`, 'error'); return; }
      }
      if ((r.origin === 'manual' || r.origin === 'extra') && !(int(r.quantity) > 0)) { showToast('Количество — больше нуля', 'error'); return; }
      if (r.limit !== '' && r.limit !== null && int(r.limit) === null) { showToast('Лимит продаж — целое число', 'error'); return; }
    }
    setSaving(true);
    try {
      for (const r of rows.filter(x => x.splitsDirty && x.line_id)) {
        // eslint-disable-next-line no-await-in-loop
        await api.setProcurementLineSplits(r.line_id, (r.splits || []).filter(x => int(x.quantity) > 0)
          .map(x => ({ color: null, product_id: x.product_id, quantity: int(x.quantity) })));
      }
      const body = {
        invoice_number: invoiceNumber.trim() || null, comment: comment || null,
        expected_date: expectedDate || null, extra_days: parseInt(extraDays, 10) || 0,
        available_percent: availablePercent !== '' ? parseInt(availablePercent, 10) : null,
        rows: rows.filter(r => r.line_id && !r.origin && !r.cancelled).map(r => {
          const out = { line_id: r.line_id };
          if (r.product_id && r.product_id !== r.orig_product_id && !r.splits) out.product_id = r.product_id;
          const p = num(r.price);
          if (p && p !== r.orig_price) out.price_rub = p;
          if (String(r.limit ?? '') !== String(r.orig_limit ?? '')) out.available_quantity = r.limit === '' ? null : int(r.limit);
          return out;
        }),
        manual_rows: rows.filter(r => r.origin === 'manual' || r.origin === 'extra').map(r => ({
          line_id: r.line_id || null, product_id: r.product_id, quantity: int(r.quantity),
          price_rub: num(r.price) || null, available_quantity: r.limit === '' ? null : int(r.limit),
        })),
        removed_line_ids: removed,
      };
      const saved = await api.saveImportForm(invoice.id, body);
      showToast(saved.repriced_orders > 0 ? `Накладная обновлена · себестоимость пересчитана в заказах: ${saved.repriced_orders}` : 'Накладная обновлена');
      onSaved(saved);
    } catch (e) {
      // Введённое не сбрасываем — человек поправит и сохранит снова (08.11)
      showToast(e.message || 'Не сохранилось', 'error');
    } finally {
      setSaving(false);
    }
  };

  const total = (rows || []).reduce((s, r) => s + (r.cancelled ? 0 : (int(r.quantity) || 0) * (num(r.price) || 0)), 0);

  return (
    <Modal title="Редактирование накладной" size="xl" onClose={close} dirty={dirty}
      confirmOverlayClose="Закрыть? Несохранённые изменения пропадут."
      footer={<>
        <button className="btn btn-secondary" onClick={close}>Отмена</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !rows}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
      </>}>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Поставщик</label>
          <input type="text" className="form-input" disabled readOnly value={invoice.supplier_name || ''} style={{ background: '#F4F4EF', color: '#888' }} />
        </div>
        <div className="form-group">
          <label className="form-label">Номер накладной</label>
          <input type="text" className="form-input" placeholder="Необязательно" value={invoiceNumber}
            onChange={e => { setInvoiceNumber(e.target.value); setDirty(true); }} />
        </div>
        <div className="form-group">
          <label className="form-label">Комментарий</label>
          <input type="text" className="form-input" value={comment} onChange={e => { setComment(e.target.value); setDirty(true); }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: 2, minWidth: 180 }}>
          <label className="form-label">Ожидаемая дата поставки *</label>
          <DatePickerCalendar value={expectedDate} onChange={(v) => { setExpectedDate(v); setDirty(true); }} minDate={mskToday()} placeholder="дд.мм.гггг" />
        </div>
        <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
          <label className="form-label" title="Заказы открываются через N дней после даты поставки">Буфер (дней до открытия)</label>
          <input type="number" className="form-input" min={0} max={30} value={extraDays} onChange={e => { setExtraDays(e.target.value); setDirty(true); }} />
        </div>
        <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
          <label className="form-label" title="% от поставки, доступный для заказов (пусто = 100%)">% доступности (пусто = 100%)</label>
          <input type="number" className="form-input" min={1} max={100} placeholder="100" value={availablePercent}
            onChange={e => { setAvailablePercent(e.target.value); setDirty(true); }} />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <label className="form-label" style={{ margin: 0 }}>Позиции</label>
          <button type="button" className="btn-link" onClick={() => setShowAdd(true)}>+ Добавить товар</button>
        </div>
        {showAdd && (
          <div style={{ marginBottom: 12 }}>
            <ProductSearchDropdown onSelect={addManual} excludeIds={[]} onClose={() => setShowAdd(false)} includeArchived />
          </div>
        )}
        {!rows ? <div className="loading"><div className="spinner"></div></div> : (
          <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9F9F6' }}>
                  <th style={{ ...th, width: 52 }} aria-label="Фото" />
                  <th style={th}>Товар</th>
                  <th style={{ ...th, textAlign: 'center', width: 80 }}>Кол-во</th>
                  <th style={{ ...th, textAlign: 'center', width: 90 }}>Лимит продаж</th>
                  <th style={{ ...th, textAlign: 'center', width: 130 }}>Цена закупки</th>
                  <th style={{ ...th, textAlign: 'right', width: 90 }}>Сумма</th>
                  <th style={{ width: 40 }} aria-label="Убрать" />
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const exchange = r.line_id && !r.origin;   // строка биржи; руками и «лишнее» — свои
                  const editable = !r.cancelled && r.editable !== false;
                  const sum = r.splits ? r.splits.reduce((s, x) => s + (int(x.quantity) || 0), 0) : 0;
                  const footer = editable && exchange ? [
                    ...(!r.product_id && r.suggested_name && !r.is_mix ? [{ label: `+ создать «${r.suggested_name}» · сохраняется сразу`, onClick: () => createProduct(r) }] : []),
                    { label: 'Несколько товаров…', onClick: () => startSplit(r) },
                  ] : null;
                  return (
                    <React.Fragment key={r.key}>
                      <tr style={{ borderTop: '1px solid #EBEDEC', color: r.cancelled ? '#aaa' : undefined, verticalAlign: 'top' }}
                        onMouseEnter={() => setFocusRow(r.key)} onFocus={() => setFocusRow(r.key)}>
                        <td style={td}>{exchange ? <Thumb url={r.photo_url} /> : null}</td>
                        <td style={td}>
                          {editable && !r.splits ? (
                            <ProductCell row={r} footer={footer} onPick={(p) => upd(r.key, { product_id: p.id, product_name: p.name, caption: r.manual_caption })} />
                          ) : (
                            <span style={{ fontWeight: 600, color: '#1C3D2E', textDecoration: r.cancelled ? 'line-through' : 'none' }}>
                              {r.splits ? `${r.product_name ? '' : ''}несколько товаров` : (r.product_name || 'Товар не выбран')}
                            </span>
                          )}
                          {exchange && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{r.cancelled ? 'отменено на бирже' : r.caption}</div>}
                          {r.origin === 'manual' && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>добавлено руками</div>}
                          {r.origin === 'extra' && <div style={{ fontSize: 12, color: '#B0603C', marginTop: 2 }}>лишнее, не заказывали</div>}
                          {editable && exchange && r.is_mix && !r.splits && (
                            <button type="button" onClick={() => startSplit(r)}
                              style={{ padding: 0, marginTop: 2, border: 'none', background: 'none', color: '#B0603C', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                              Микс — выбрать несколько товаров →
                            </button>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {exchange
                            ? <span title="Количество ведёт биржа">{r.stems}</span>
                            : <input type="text" inputMode="numeric" value={r.quantity} aria-label="Количество"
                              onChange={e => upd(r.key, { quantity: e.target.value.replace(/\D/g, '') })} style={cellInput} />}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <input type="text" inputMode="numeric" value={r.limit ?? ''} placeholder="—" aria-label="Лимит продаж" disabled={!editable}
                            onChange={e => upd(r.key, { limit: e.target.value.replace(/\D/g, '') })} style={cellInput} />
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <div style={{ position: 'relative' }}>
                            <input type="text" inputMode="decimal" value={r.price} placeholder="0" aria-label="Цена закупки, ₽ за шт" disabled={!editable}
                              title={r.formula || undefined}
                              onChange={e => upd(r.key, { price: e.target.value.replace(/[^\d,.]/g, '') })}
                              style={{ ...cellInput, paddingRight: 34, border: `1px solid ${!r.price && !r.cancelled ? '#B0603C' : '#ddd'}`, '--stroke': !r.price && !r.cancelled ? '#B0603C' : '#ddd' }} />
                            <span aria-hidden="true" style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#909894' }}>₽/шт</span>
                          </div>
                          {r.price_cur_manual != null && r.manual_price != null && (
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }} title={r.formula || undefined}>{r.currency}{fmtCur(r.price_cur_manual)} → {fmtRub(r.manual_price)} ₽</div>
                          )}
                          {/* Место под «по цене биржи…» есть у каждой строки, видно — у строки под курсором: иначе
                              строки прыгают при наведении и клик по лупе ниже промахивается (04.11) */}
                          {exchange && editable && !r.cancelled && (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 4,
                              visibility: focusRow === r.key || curDraft[r.key] ? 'visible' : 'hidden' }}>
                              <input type="text" inputMode="decimal" placeholder={`${r.currency}/шт`} value={curDraft[r.key] ?? ''} aria-label="Цена биржи за шт"
                                onChange={e => setCurDraft(d => ({ ...d, [r.key]: e.target.value.replace(/[^\d,.]/g, '') }))}
                                onKeyDown={e => { if (e.key === 'Enter') calcPrice(r); }}
                                style={{ width: 60, padding: '3px 5px', textAlign: 'center', border: '1px solid #ddd', fontSize: 12 }} />
                              <button type="button" className="btn-link" style={{ fontSize: 11, color: '#909894' }} onClick={() => calcPrice(r)}
                                title="Сохраняется сразу">по цене биржи…</button>
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>
                          {r.cancelled ? '—' : num(r.price) ? `${fmtRub((int(r.quantity) || 0) * num(r.price))} ₽` : ''}
                        </td>
                        <td style={{ padding: 4 }}>
                          {exchange ? (
                            // Не disabled: так кнопка в табуляции и причина доходит до читалки (aria-describedby)
                            <>
                              <button type="button" className="btn-icon" aria-disabled="true" aria-label="Удалить строку"
                                aria-describedby={`why-${r.key}`} title="Строку биржи удалить нельзя — отмените покупку на бирже"
                                style={{ opacity: 0.35, cursor: 'not-allowed' }}
                                onClick={() => showToast('Строку биржи удалить нельзя — отмените покупку на бирже', 'info')}>
                                <TrashIcon />
                              </button>
                              <span id={`why-${r.key}`} style={srOnly}>Строку биржи удалить нельзя — отмените покупку на бирже</span>
                            </>
                          ) : r.origin === 'extra' ? (
                            <button type="button" className="btn-icon is-danger" title="Убрать лишнее — сразу" aria-label="Убрать лишнее"
                              onClick={() => removeExtra(r)}>
                              <TrashIcon />
                            </button>
                          ) : (
                            <button type="button" className="btn-icon is-danger" title="Убрать" aria-label="Убрать"
                              onClick={() => { setDirty(true); if (r.line_id) setRemoved(x => [...x, r.line_id]); setRows(rs => rs.filter(x => x.key !== r.key)); }}>
                              <TrashIcon />
                            </button>
                          )}
                        </td>
                      </tr>
                      {r.splits && (
                        <>
                          {r.splits.map((sp, i) => (
                            <tr key={sp.key}>
                              <td />
                              <td style={{ ...td, paddingLeft: 26, paddingTop: 2 }}>
                                ↳ <ProductCell row={{ ...sp, line_id: r.line_id }} title="Товар"
                                  onPick={(p) => upd(r.key, { splitsDirty: true, splits: r.splits.map((x, j) => (j === i ? { ...x, product_id: p.id, product_name: p.name } : x)) })} />
                              </td>
                              <td style={{ ...td, paddingTop: 2 }}>
                                <input type="text" inputMode="numeric" value={sp.quantity} aria-label="Сколько, шт"
                                  onChange={e => upd(r.key, { splitsDirty: true, splits: r.splits.map((x, j) => (j === i ? { ...x, quantity: e.target.value.replace(/\D/g, '') } : x)) })}
                                  style={cellInput} />
                              </td>
                              <td colSpan={3} />
                              <td style={{ padding: 4 }}>
                                <button type="button" className="btn-icon is-danger" aria-label="Убрать товар"
                                  onClick={() => upd(r.key, { splitsDirty: true, splits: r.splits.filter((_, j) => j !== i) })}>×</button>
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td />
                            <td colSpan={6} style={{ ...td, paddingLeft: 26, paddingTop: 2 }}>
                              <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                <SplitAddButton lineId={r.line_id} exclude={r.splits.map(x => x.product_id).filter(Boolean)}
                                  onAdd={(ps) => upd(r.key, { splitsDirty: true, splits: [...r.splits, ...ps.map(p => ({ key: `n${p.id}${Date.now()}`, product_id: p.id, product_name: p.name, quantity: '0' }))] })} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: sum === r.stems ? '#1F763B' : '#B0603C' }}>разложено {sum} из {r.stems}</span>
                                <button type="button" className="btn-link" style={{ fontSize: 12 }} onClick={() => upd(r.key, { splits: null, splitsDirty: true })}>Один товар</button>
                              </span>
                            </td>
                          </tr>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 10, background: '#F9F9F6', borderTop: '1px solid #eee', fontWeight: 600, fontSize: 14 }}>
              Итого: {fmtRub(total)} ₽
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── «Принять поставку» — для всех плановых (флаг включён) ──────────────

function Stars3({ l, vaseFrom }) {
  const m = marks(l, vaseFrom);
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{m ? m.replace(/^оценки /, '') : ''}</span>;
}

export function ReceivingAcceptModal({ invoice, role, onClose, onAccepted, showToast }) {
  const narrow = useNarrow();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});        // id строки → { actual, defect, price } — ввод, ещё не сохранён
  const [savingIds, setSavingIds] = useState(() => new Set());
  const [rowError, setRowError] = useState({});
  const [conflicts, setConflicts] = useState({});  // id строки → свежая строка (409): сохранение ждёт [Взять]/[Оставить]
  const [expanded, setExpanded] = useState(null);
  const [hiddenFor, setHiddenFor] = useState(null);
  const [editing, setEditing] = useState(null);    // узкий экран: строка в окне
  const [confirm, setConfirm] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState(invoice.invoice_number || '');
  const [actualDate, setActualDate] = useState(mskToday());
  const [comment, setComment] = useState(invoice.comment || '');
  const [extraOpen, setExtraOpen] = useState(false);
  const extraRef = useRef(null);
  const isAdmin = role === 'admin' || role === 'owner';
  // Последние значения — для цепочки сохранений (между await состояние в замыкании устаревает)
  const linesRef = useRef({});       // id → строка с сервера (свежая версия)
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const conflictsRef = useRef(conflicts);
  conflictsRef.current = conflicts;
  const chainRef = useRef({});       // id → сохранение строки в полёте: следующее ждёт его
  const reqRef = useRef(0);          // номер последнего запроса: применяем только его ответ

  const load = useCallback(() => {
    const n = ++reqRef.current;
    return api.getReceivingInvoice(invoice.id)
      .then(d => {
        if (n !== reqRef.current) return;   // пока шёл, ушёл запрос новее — его ответ и покажем
        d.lines.forEach(l => { linesRef.current[l.id] = l; });
        setData(d);
        setError(null);
        setInvoiceNumber(v => v || d.invoice_number || d.invoice_number_suggest || '');
      })
      .catch(e => { if (n === reqRef.current) setError(e.message || 'Не загрузилось'); });
  }, [invoice.id]);
  useEffect(() => { load(); }, [load]);
  // Строки принимают несколько человек — раз в 15 с свежие данные (свой ввод не трогаем); пока
  // сохраняем, принимаем или спрашиваем «Принять?» — не перечитываем
  const pollPaused = savingIds.size > 0 || !!expanded || !!editing || accepting || confirm || bulkConfirm || bulkRunning;
  useEffect(() => {
    if (pollPaused) return undefined;
    const t = setInterval(() => load(), 15000);
    return () => clearInterval(t);
  }, [load, pollPaused]);

  // «Принять на склад» сразу после ввода: поле сохраняется при уходе из него — кнопка ждёт сохранения
  // и проверяет заново (свежий рендер), а не просит нажать второй раз
  const [acceptQueued, setAcceptQueued] = useState(false);
  const clickAcceptRef = useRef(null);
  useEffect(() => {
    if (!acceptQueued) return;
    setAcceptQueued(false);
    clickAcceptRef.current?.(true);
  }, [acceptQueued]);

  const loadExtraOptions = useCallback(() => api.getProductsAll(true).then(d => ({
    similar: [], products: (d.items || d).map(p => ({ id: p.id, name: p.name, is_active: p.is_active !== false, is_hidden: !!p.is_hidden, height_cm: p.height_cm })),
  })), []);
  const closeExtra = useCallback(() => setExtraOpen(false), []);

  const hasUnsaved = Object.values(drafts).some(d => Object.keys(d).length > 0) || savingIds.size > 0 || Object.keys(conflicts).length > 0;
  const askClose = async () => {
    if (hasUnsaved && !(await nfConfirm('Есть несохранённые строки — введённое пропадёт.', { title: 'Закрыть приёмку?', confirmText: 'Закрыть', cancelText: 'Остаться' }))) return;
    onClose();
  };

  if (error || !data) {
    return (
      <Modal title="Приёмка поставки" size="xl" onClose={onClose}
        footer={<button type="button" className="btn btn-secondary" onClick={onClose}>Закрыть</button>}>
        {error ? <div style={{ color: '#B0603C' }}>{error} <button type="button" className="btn-link" onClick={load}>Повторить</button></div>
          : <div className="loading"><div className="spinner"></div></div>}
      </Modal>
    );
  }

  const accepted = data.status === 'accepted';
  const vaseFrom = data.vase_open ? null : data.vase_from;
  const draftOf = (l) => drafts[l.id] || {};
  const setDraft = (l, patch) => setDrafts(d => ({ ...d, [l.id]: { ...(d[l.id] || {}), ...patch } }));
  const dropDraft = (id) => setDrafts(d => { if (!d[id]) return d; const n = { ...d }; delete n[id]; return n; });
  // Сохранили — убираем только сохранённые поля и только если их не меняли, пока шло сохранение
  const clearSaved = (id, saved) => setDrafts(d => {
    const cur = d[id];
    if (!cur) return d;
    const next = { ...cur };
    Object.entries(saved).forEach(([k, v]) => { if (v !== undefined && next[k] === v) delete next[k]; });
    const out = { ...d };
    if (Object.keys(next).length) out[id] = next; else delete out[id];
    return out;
  });
  const markSaving = (id, on) => setSavingIds(cur => { const n = new Set(cur); if (on) n.add(id); else n.delete(id); return n; });
  const putLine = (fresh) => {
    linesRef.current[fresh.id] = fresh;
    setData(d => (d ? { ...d, lines: d.lines.map(x => (x.id === fresh.id ? fresh : x)) } : d));
  };
  const setConflict = (id, fresh) => {
    conflictsRef.current = { ...conflictsRef.current, [id]: fresh };
    setConflicts(c => ({ ...c, [id]: fresh }));
  };
  const clearConflict = (id) => {
    const n = { ...conflictsRef.current };
    delete n[id];
    conflictsRef.current = n;
    setConflicts(c => { const m = { ...c }; delete m[id]; return m; });
  };
  // Сохранения одной строки — строго по очереди: второе ждёт первое и берёт его версию
  const chain = (id, fn) => {
    const prev = chainRef.current[id] || Promise.resolve();
    const p = prev.catch(() => {}).then(fn);
    const tail = p.catch(() => {}).finally(() => { if (chainRef.current[id] === tail) delete chainRef.current[id]; });
    chainRef.current[id] = tail;
    return p;
  };

  const doSaveQty = async (id) => {
    const l = linesRef.current[id];
    if (!l || conflictsRef.current[id]) return;   // 409 — ждём [Взять её данные] / [Оставить мои]
    const dr = draftsRef.current[id] || {};
    if (dr.actual === undefined && dr.defect === undefined) return;
    const actual = dr.actual !== undefined ? int(dr.actual) : (l.actual_quantity ?? l.stems);
    const defect = dr.defect !== undefined ? (int(dr.defect) || 0) : (l.defect_quantity || 0);
    if (actual === null) { setRowError(e => ({ ...e, [id]: 'Факт — целое число' })); return; }
    if (defect > actual) { setRowError(e => ({ ...e, [id]: 'Брака больше, чем пришло' })); return; }
    if (l.accepted && actual === l.actual_quantity && defect === (l.defect_quantity || 0)) {
      clearSaved(id, { actual: dr.actual, defect: dr.defect });   // то же, что сохранено, — не сохраняем
      return;
    }
    if (defect > 0 && (!l.defect_reason || !l.photos.length)) {
      // Брак без причины и фото не сохранить — раскрываем строку с введённым: там причина и фото
      setRowError(e => ({ ...e, [id]: 'Укажите причину и фото брака — строка раскрыта' }));
      setExpanded(id);
      return;
    }
    markSaving(id, true);
    setRowError(e => ({ ...e, [id]: null }));
    try {
      const res = await api.saveReceivingLine(id, { version: l.version, actual_quantity: actual, defect_quantity: defect,
        defect_reason: defect > 0 ? l.defect_reason : null, defect_note: defect > 0 ? l.defect_note : null });
      if (res?.line) putLine(res.line);
      clearSaved(id, { actual: dr.actual, defect: dr.defect });
      load();
    } catch (e) {
      const fresh = e.response?.data?.line;
      if (fresh) setConflict(id, fresh);
      else setRowError(er => ({ ...er, [id]: e.message || 'Не сохранилось' }));
    } finally {
      markSaving(id, false);
    }
  };
  const saveInline = (l) => chain(l.id, () => doSaveQty(l.id));
  const savePrice = (l) => chain(l.id, async () => {
    const v = draftsRef.current[l.id]?.price;
    if (v === undefined) return;
    const p = num(v);
    if (!p || p <= 0) { setRowError(e => ({ ...e, [l.id]: 'Цена — больше нуля' })); return; }
    markSaving(l.id, true);
    try {
      const res = await api.setProcurementLinePrice(l.id, { price_rub: p });
      clearSaved(l.id, { price: v });
      if (res?.repriced_orders > 0) showToast(`Себестоимость пересчитана в заказах: ${res.repriced_orders}`, 'info');
      await load();   // цена меняет версию строки — следующее сохранение возьмёт свежую
    } catch (e) {
      setRowError(er => ({ ...er, [l.id]: e.message || 'Цена не сохранилась' }));
    } finally {
      markSaving(l.id, false);
    }
  });
  // «Оставить мои» — свой ввод поверх СВЕЖЕЙ строки (её версия, причина, фото)
  const keepMine = (l) => {
    const fresh = conflictsRef.current[l.id];
    clearConflict(l.id);
    if (fresh) putLine(fresh);
    saveInline(l);
  };
  const takeTheirs = (l) => {
    const fresh = conflictsRef.current[l.id];
    clearConflict(l.id);
    dropDraft(l.id);
    if (fresh) putLine(fresh);
    load();
  };
  const addExtra = async (p) => {
    setExtraOpen(false);
    try {
      await api.addReceivingExtra(invoice.id, p.id, 1);
      showToast(`«${p.name}» — лишнее, не заказывали: укажите, сколько пришло`);
      load();
    } catch (e) {
      showToast(e.message || 'Не добавилось', 'error');
    }
  };

  const problems = data.problems || [];
  const needNumber = data.invoice_number_required && !invoiceNumber.trim();
  const blockReason = !isAdmin ? 'Принимает на склад администратор или владелец'
    : hasUnsaved ? 'Есть несохранённые строки — дождитесь «сохраняю…» или ответьте на «изменил эту строку»'
      : problems.length ? problems.join(' · ') : needNumber ? 'Укажите номер накладной поставщика' : null;
  const statusLine = [
    data.lines_not_accepted ? `Не принято ${data.lines_not_accepted}` : null,
    data.defect_without_photo ? `брак без фото ${data.defect_without_photo}` : null,
    data.lines_without_product ? `без товара ${data.lines_without_product}` : null,
    data.price_missing_count ? `нет цены ${data.price_missing_count}` : null,
  ].filter(Boolean).join(' · ');

  // sendClaims — «Принять и отправить рекламацию»: товар на склад, потом срок у Market и отправка (04.11)
  const accept = async (sendClaims = false) => {
    setConfirm(false);
    if (accepting || bulkRunning || hasUnsaved) return;   // второй клик / Enter, пока идёт, — не второй приём
    setAccepting(true);
    try {
      const res = await api.acceptProcurementInvoice(invoice.id, data.versions, {
        invoice_number: invoiceNumber.trim() || null, date: actualDate, comment: comment || null, send_claims: sendClaims,
      });
      const due = res.deadline_at ? new Date(res.deadline_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null;
      let text = 'Поставка принята';
      let tone = res.warnings?.length ? 'warning' : 'success';
      if (res.claims_total > 0 && !sendClaims) {
        text = `Поставка принята. Рекламация не отправлена — отправить можно в накладной${due ? ` до ${due}` : ''}`;
      } else if (sendClaims && res.claims_late > 0 && !res.claims_sent && !res.claims_pending) {
        text = `Товар принят на склад. ${res.late_text || 'Срок подачи рекламации вышел'}`;
        tone = 'warning';
      } else if (sendClaims) {
        text = res.claims_pending && !res.claims_sent ? 'Поставка принята. Рекламация уйдёт в Market, как только он ответит'
          : 'Поставка принята. Рекламация отправлена';
        if (res.claims_late > 0) { text += `. ${res.late_text || 'По части строк срок подачи вышел'}`; tone = 'warning'; }
      }
      showToast(text, tone);
      onAccepted();
    } catch (e) {
      showToast(e.message || 'Не принялось', 'error');
      const stale = (e.message || '').startsWith('Пока вы смотрели');
      await load();
      if (stale) setConfirm(true);
    } finally {
      setAccepting(false);
    }
  };
  const clickAccept = async (afterSave = false) => {
    if (accepting || bulkRunning) return;
    const pending = Object.values(chainRef.current);
    if (!afterSave && hasUnsaved && pending.length && !Object.keys(conflictsRef.current).length) {
      await Promise.all(pending.map(p => p.catch(() => {})));
      setAcceptQueued(true);
      return;
    }
    if (blockReason) {
      const first = data.lines.find(l => !l.cancelled && (!l.accepted || l.needs_photo));
      if (first) document.getElementById(`arow-${first.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setConfirm(true);
  };
  clickAcceptRef.current = clickAccept;

  // «Принять оставшиеся как в плане»: не принятые строки без брака и без своего ввода — факт = план, брак 0.
  // Микс без товара сервер так не примет — его раскладывают «Несколькими товарами»
  const bulkRows = accepted ? [] : data.lines.filter(l => !l.cancelled && !l.accepted && !l.extra && !(l.splits || []).length && !l.mix_hint
    && !Object.keys(draftOf(l)).length && !conflicts[l.id] && !savingIds.has(l.id));
  const runBulk = async () => {
    setBulkConfirm(false);
    if (bulkRunning || accepting) return;
    setBulkRunning(true);
    const saveAsPlan = async (id) => {
      const cur = linesRef.current[id];
      if (!cur || cur.accepted || conflictsRef.current[id]) return null;
      markSaving(id, true);
      try {
        const res = await api.saveReceivingLine(id, { version: cur.version, actual_quantity: cur.stems, defect_quantity: 0,
          defect_reason: null, defect_note: null });
        if (res?.line) putLine(res.line);
        return true;
      } catch (e) {
        const fresh = e.response?.data?.line;
        if (fresh) setConflict(id, fresh);
        else setRowError(er => ({ ...er, [id]: e.message || 'Не сохранилось' }));
        return false;
      } finally {
        markSaving(id, false);
      }
    };
    const results = [];
    for (const l of bulkRows) {
      // по одной строке и через ту же очередь, что ввод в строке
      // eslint-disable-next-line no-await-in-loop
      results.push(await chain(l.id, () => saveAsPlan(l.id)));
    }
    const ok = results.filter(r => r === true).length;
    const failed = results.filter(r => r === false).length;
    setBulkRunning(false);
    await load();
    showToast(failed ? `Принято строк: ${ok}, не сохранилось: ${failed} — см. строки` : `Принято строк: ${ok} — факт как в плане`, failed ? 'warning' : 'success');
  };

  const people = data.people_now || [];
  const header = (
    <>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Поставщик</label>
          <input type="text" className="form-input" disabled readOnly value={data.supplier_name || ''} style={{ background: '#F4F4EF', color: '#888' }} />
        </div>
        <div className="form-group">
          <label className="form-label">№ накладной{data.invoice_number_required ? ' *' : ''}</label>
          <input type="text" className="form-input" value={invoiceNumber} disabled={accepted}
            placeholder={data.invoice_number_required ? 'Введите номер' : 'Необязательно'} onChange={e => setInvoiceNumber(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Факт. дата</label>
          <DatePickerCalendar value={actualDate} onChange={setActualDate} maxDate={mskToday()} disabled={accepted} />
        </div>
        <div className="form-group">
          <label className="form-label">Комментарий</label>
          <input type="text" className="form-input" value={comment} disabled={accepted} onChange={e => setComment(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: '#5E6561', margin: '8px 0 12px' }}>
        <span style={{ fontWeight: 600, color: '#1C3D2E' }}>Принято строк {data.lines_accepted} из {data.lines_total}</span>
        {people.length > 0 && (
          <span><span style={{ color: '#1F763B' }}>●</span> {people.join(' и ')} {people.length > 1 ? 'принимают' : 'принимает'} сейчас · обновлено {hhmm(data.refreshed_at)}</span>
        )}
        {!data.receiving_open && !accepted && <span>флористам приёмка откроется {ddmm(data.expected_date)} — в день поставки</span>}
        {bulkRows.length > 0 && (
          <button type="button" className="btn-link" disabled={bulkRunning || accepting} onClick={() => setBulkConfirm(true)}>
            {bulkRunning ? 'Принимаю строки…' : `Принять оставшиеся как в плане (${bulkRows.length})`}
          </button>
        )}
        {!accepted && (
          <>
            <button ref={extraRef} type="button" className="btn-link" onClick={() => setExtraOpen(o => !o)}>+ Лишнее</button>
            {extraOpen && <ProductPickerPopover anchorRef={extraRef} loadOptions={loadExtraOptions} onClose={closeExtra} title="Лишнее — не заказывали" onSelect={addExtra} />}
          </>
        )}
      </div>
    </>
  );

  const rowFor = (l) => {
    const dr = draftOf(l);
    const isOpen = expanded === l.id;
    const editable = !accepted && !l.cancelled;
    const inlineQty = editable && !l.splits.length && !isOpen;
    const price = dr.price !== undefined ? dr.price : (l.price != null ? String(l.price).replace('.', ',') : '');
    const qtyShown = l.accepted ? l.actual_quantity : null;
    const sum = (l.price && (qtyShown ?? l.stems)) ? fmtRub((qtyShown ?? l.stems) * l.price) : '';
    return (
      <React.Fragment key={l.id}>
        <tr id={`arow-${l.id}`} style={{ borderTop: '1px solid #EBEDEC', color: l.cancelled ? '#aaa' : undefined, verticalAlign: 'top',
          background: l.accepted ? '#F3F7F4' : undefined }}>
          <td style={{ ...td, width: 28 }}>
            {!l.cancelled && (
              <button type="button" aria-expanded={isOpen} aria-label={isOpen ? 'Свернуть строку' : 'Раскрыть строку'}
                onClick={() => { if (isOpen) dropDraft(l.id); setExpanded(isOpen ? null : l.id); }}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#5E6561', fontSize: 13, padding: 0, transition: 'transform .18s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▸</button>
            )}
          </td>
          <td style={td}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              {l.lot_photo_url && <Thumb url={l.lot_photo_url} size={36} />}
              <div>
                <div style={{ fontWeight: 600, color: '#1C3D2E', textDecoration: l.cancelled ? 'line-through' : 'none' }}>{lineTitle(l)}</div>
                <div style={{ fontSize: 12, color: l.extra ? '#B0603C' : '#888' }}>
                  {l.extra ? 'лишнее, не заказывали' : l.origin ? '' : `${l.lot_name || ''}${l.length_cm ? `, ${l.length_cm} см` : ''}`}
                </div>
                {l.hidden_defects?.length > 0 && <div style={{ fontSize: 12, color: '#CB4D45' }}>после приёмки {hiddenText(l)}</div>}
              </div>
            </div>
          </td>
          <td style={{ ...td, textAlign: 'center' }}>{l.extra ? '—' : l.stems}</td>
          <td style={{ ...td, textAlign: 'center', color: '#888' }}>{l.sale_limit ?? '—'}</td>
          <td style={{ ...td, textAlign: 'center', width: 72 }}>
            {inlineQty ? (
              <input type="text" inputMode="numeric" aria-label="Факт, шт" value={dr.actual ?? (l.actual_quantity ?? (l.extra ? '' : l.stems))}
                onChange={e => setDraft(l, { actual: e.target.value.replace(/\D/g, '') })} onBlur={() => saveInline(l)}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} style={cellInput} />
            ) : <span style={{ fontWeight: 600 }}>{l.actual_quantity ?? '—'}</span>}
          </td>
          <td style={{ ...td, textAlign: 'center', width: 64 }}>
            {inlineQty ? (
              <input type="text" inputMode="numeric" aria-label="Брак, шт" value={dr.defect ?? (l.defect_quantity || 0)}
                onChange={e => setDraft(l, { defect: e.target.value.replace(/\D/g, '') })} onBlur={() => saveInline(l)}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} style={cellInput} />
            ) : <span style={{ color: l.defect_quantity ? '#CB4D45' : '#999' }}>{l.defect_quantity || 0}</span>}
          </td>
          <td style={{ ...td, fontSize: 12 }}>{l.defect_reason_label || ''}</td>
          <td style={td}>
            <div style={{ display: 'flex', gap: 3 }}>
              {l.photos.slice(0, 3).map(ph => <img key={ph.id} src={photoSrc(ph.url)} alt="" style={{ width: 26, height: 26, objectFit: 'cover' }} />)}
              {l.photos.length > 3 && <span style={{ fontSize: 11, color: '#909894' }}>+{l.photos.length - 3}</span>}
              {l.needs_photo && <span style={{ fontSize: 11, color: '#B0603C' }}>нет фото</span>}
            </div>
          </td>
          <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>{l.accepted ? <Stars3 l={l} vaseFrom={vaseFrom} /> : ''}</td>
          <td style={{ ...td, width: 96 }}>
            {isAdmin && l.price_editable && !l.cancelled ? (
              <input type="text" inputMode="decimal" aria-label="Цена, ₽ за шт" value={price} placeholder="₽/шт"
                onChange={e => setDraft(l, { price: e.target.value.replace(/[^\d,.]/g, '') })} onBlur={() => savePrice(l)}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                style={{ ...cellInput, border: `1px solid ${l.price == null ? '#B0603C' : '#ddd'}` }} />
            ) : (l.price != null ? `${fmtRub(l.price)} ₽` : '')}
          </td>
          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{sum ? `${sum} ₽` : ''}</td>
          <td style={{ ...td, fontSize: 12, whiteSpace: 'nowrap' }}>
            {savingIds.has(l.id) ? <span style={{ color: '#909894' }}>сохраняю…</span>
              : l.accepted ? <>{l.edited_by_name || l.accepted_by_name}<div style={{ color: '#909894' }}>{hhmm(l.edited_at || l.accepted_at)}</div></>
                : l.opened_by_name ? <span style={{ color: '#7E5D00' }}>✎ {l.opened_by_name}</span> : ''}
            {accepted && isAdmin && !l.cancelled && <div><RowMenu onHidden={() => setHiddenFor(l)} /></div>}
          </td>
        </tr>
        {(rowError[l.id] || conflicts[l.id]) && (
          <tr>
            <td />
            <td colSpan={11} style={{ padding: '0 10px 8px' }}>
              {conflicts[l.id] && <ConflictNote fresh={conflicts[l.id]} onTake={() => takeTheirs(l)} onKeep={() => keepMine(l)} />}
              {rowError[l.id] && <div style={{ fontSize: 12, color: '#CB4D45' }}>{rowError[l.id]}</div>}
            </td>
          </tr>
        )}
        {(l.splits || []).map(sp => (
          <tr key={`s${sp.id}`}>
            <td />
            <td style={{ ...td, paddingLeft: 26, fontSize: 12, paddingTop: 2 }}>↳ {sp.product_name || 'без товара'}</td>
            <td style={{ ...td, textAlign: 'center', fontSize: 12, paddingTop: 2 }}>{sp.quantity || '—'}</td>
            <td />
            <td style={{ ...td, textAlign: 'center', fontSize: 12, paddingTop: 2 }}>{sp.actual_quantity ?? '—'}</td>
            <td style={{ ...td, textAlign: 'center', fontSize: 12, paddingTop: 2 }}>{sp.defect_quantity || 0}</td>
            <td colSpan={6} />
          </tr>
        ))}
        {isOpen && (
          <tr>
            <td />
            <td colSpan={11} style={{ padding: '4px 10px 12px' }}>
              <ReceivingLineEditor line={l} invoice={data} showToast={showToast} embedded canPlan={isAdmin}
                initialDraft={{ actual: dr.actual, defect: dr.defect }}
                onSaved={(fresh) => { if (fresh) putLine(fresh); dropDraft(l.id); setExpanded(null); setRowError(e => ({ ...e, [l.id]: null })); load(); }} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <>
      <Modal title={`Приёмка поставки — ${data.display_title || data.title}`} size="xl" onClose={onClose}
        dirty={hasUnsaved} dirtyOnInput={false} confirmOverlayClose="Есть несохранённые строки — введённое пропадёт."
        footer={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#5E6561' }}>
                {accepted ? `Принята · на склад ${data.to_stock} шт, брак ${data.defect_total} шт` : statusLine || 'Все строки приняты'}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={askClose}>Закрыть</button>
                {!accepted && (
                  <button type="button" className="btn btn-primary" disabled={accepting || bulkRunning}
                    aria-disabled={!!blockReason || accepting} aria-describedby={blockReason ? 'accept-block-reason' : undefined}
                    style={blockReason ? { opacity: 0.5, cursor: 'not-allowed' } : undefined} onClick={() => clickAccept()}>
                    {accepting ? 'Зачисление…' : 'Принять на склад'}
                  </button>
                )}
              </div>
            </div>
            {!accepted && blockReason && <div id="accept-block-reason" role="status" style={{ fontSize: 12, color: '#B0603C', textAlign: 'right' }}>{blockReason}</div>}
          </div>
        }>
        {header}
        {narrow ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.lines.map(l => <ReceivingLineSummary key={l.id} line={l} onOpen={setEditing} vaseFrom={vaseFrom} />)}
          </div>
        ) : (
          <div style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F9F9F6' }}>
                  <th style={{ ...th, width: 28 }} aria-label="Раскрыть" />
                  <th style={th}>Товар</th>
                  <th style={{ ...th, textAlign: 'center' }}>План</th>
                  <th style={{ ...th, textAlign: 'center' }}>Лимит</th>
                  <th style={{ ...th, textAlign: 'center' }}>Факт</th>
                  <th style={{ ...th, textAlign: 'center' }}>Брак</th>
                  <th style={th}>Причина</th>
                  <th style={th}>Фото</th>
                  <th style={th} title="Свежесть · соответствие · ваза">Оценки</th>
                  <th style={th}>Цена</th>
                  <th style={{ ...th, textAlign: 'right' }}>Сумма</th>
                  <th style={th}>Принял</th>
                </tr>
              </thead>
              <tbody>{data.lines.map(rowFor)}</tbody>
            </table>
          </div>
        )}
      </Modal>

      {editing && (
        <Modal title="Строка поставки" size="large" onClose={() => setEditing(null)}>
          <ReceivingLineEditor line={editing} invoice={data} showToast={showToast} canPlan={isAdmin} onSaved={() => { setEditing(null); load(); }} />
        </Modal>
      )}
      {hiddenFor && (
        <HiddenDefectModal line={hiddenFor} showToast={showToast} onClose={() => setHiddenFor(null)} onDone={() => { setHiddenFor(null); load(); }} />
      )}
      {confirm && (
        <ConfirmDialog title="Принять на склад?"
          message={`На склад ${data.to_stock} шт · брак ${data.defect_total} · рекламаций ${data.claims_count || 0}. После принятия изменить нельзя.`
            + ((data.notes || []).length ? ` ${data.notes.map(n => n[0].toUpperCase() + n.slice(1)).join('. ')}.` : '')}
          {...(data.claims_count > 0
            ? { confirmText: 'Принять и отправить рекламацию', altText: 'Принять без рекламации',
              onConfirm: () => accept(true), onAlt: () => accept(false) }
            : { confirmText: 'Принять на склад', onConfirm: () => accept(false) })}
          variant="success" onCancel={() => setConfirm(false)} />
      )}
      {bulkConfirm && (
        <ConfirmDialog title="Принять оставшиеся как в плане?"
          message={`Строк: ${bulkRows.length}. Факт = план, брак 0. Строки с браком, «несколькими товарами» и вашим вводом не трогаем.`}
          confirmText={`Принять ${bulkRows.length}`} variant="success" onConfirm={runBulk} onCancel={() => setBulkConfirm(false)} />
      )}
    </>
  );
}

